/**
 * Range-rings overlay — concentric distance rings centred on own ship, with range
 * labels and optional cardinal (N/E/S/W) spokes. The circles are true distances
 * at the boat's latitude (rhumb walks from the centre), recomputed as the boat
 * moves. Pure rendering: MapView passes the live centre and the user's settings.
 */
import type { Map as MaplibreMap, GeoJSONSource } from 'maplibre-gl';
import type { Feature, FeatureCollection } from 'geojson';
import { rhumbDestination, rhumbCircle } from '@/nav';
import { getPalette } from './style/palettes';
import type { Palette } from '@/store';

const SOURCE_ID = 'range-rings';

// Bottom -> top. moveLayer with no `before` moves a layer to the top, so walking
// the list in this order leaves the labels highest.
const LAYER_IDS = ['range-rings-spoke', 'range-rings-ring', 'range-rings-label'];

export interface RangeRingsOpts {
  on: boolean;
  centre: [number, number] | null;
  intervalNm: number;
  count: number;
  spokes: boolean;
}

/** A short ring label, e.g. "0.5 nm" / "1.0 nm". */
function ringLabel(nm: number): string {
  return `${nm.toFixed(nm < 10 ? 1 : 0)} nm`;
}

function build(o: RangeRingsOpts): FeatureCollection {
  if (!o.on || !o.centre || o.count < 1 || o.intervalNm <= 0) {
    return { type: 'FeatureCollection', features: [] };
  }
  const features: Feature[] = [];
  const maxR = o.intervalNm * o.count;
  for (let i = 1; i <= o.count; i++) {
    const r = o.intervalNm * i;
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: rhumbCircle(o.centre, r) },
      properties: { kind: 'ring' },
    });
    // Label on the NE diagonal so it dodges the cardinal spokes.
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: rhumbDestination(o.centre, 45, r) },
      properties: { kind: 'label', label: ringLabel(r) },
    });
  }
  if (o.spokes) {
    for (const brg of [0, 90, 180, 270]) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [o.centre, rhumbDestination(o.centre, brg, maxR)] },
        properties: { kind: 'spoke' },
      });
    }
  }
  return { type: 'FeatureCollection', features };
}

const EMPTY: RangeRingsOpts = { on: false, centre: null, intervalNm: 0.5, count: 0, spokes: false };

export function ensureRangeRingsLayer(map: MaplibreMap, palette: Palette): void {
  const p = getPalette(palette);
  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, { type: 'geojson', data: build(EMPTY) });
  }
  if (!map.getLayer('range-rings-spoke')) {
    map.addLayer({
      id: 'range-rings-spoke',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'spoke'],
      paint: { 'line-color': p.CHGRD, 'line-width': 0.8, 'line-opacity': 0.5, 'line-dasharray': [2, 3] },
    });
  }
  if (!map.getLayer('range-rings-ring')) {
    map.addLayer({
      id: 'range-rings-ring',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'ring'],
      paint: { 'line-color': p.CHGRD, 'line-width': 1, 'line-opacity': 0.65 },
    });
  }
  if (!map.getLayer('range-rings-label')) {
    map.addLayer({
      id: 'range-rings-label',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'label'],
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['Open Sans Regular'],
        'text-size': 10,
        'text-allow-overlap': true,
      },
      paint: { 'text-color': p.CHBLK, 'text-halo-color': p.UI_BG, 'text-halo-width': 1.4 },
    });
  }
  raiseRangeRingsLayers(map);
}

/**
 * Lift the range-ring layers back to the top of the stack. Same reasoning as the
 * ownship/route/EBL-VRM overlays: the rings are created before the async chart
 * cells finish, and later a satellite overlay toggle, provider switch or palette
 * swap re-inserts the satellite raster (and opaque chart fills) ABOVE these lines,
 * hiding them. The other overlays pop back because their update path re-raises on
 * every fix; the range-ring update did not, so once buried they stayed hidden
 * even with the imagery on. Re-seating on both the ensure and the update path
 * keeps them above the satellite raster and chart fills. Missing layers are
 * skipped.
 */
export function raiseRangeRingsLayers(map: MaplibreMap): void {
  for (const id of LAYER_IDS) {
    if (map.getLayer(id)) map.moveLayer(id);
  }
}

export function updateRangeRingsLayer(map: MaplibreMap, o: RangeRingsOpts): void {
  const src = map.getSource(SOURCE_ID);
  if (src) (src as GeoJSONSource).setData(build(o));
  raiseRangeRingsLayers(map);
}
